"use client";

import React, { useState, useMemo, useEffect } from "react";
import { useSession, signIn } from "next-auth/react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
  QueryFunction,
  UseQueryResult,
  UseMutationResult
} from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"; // shadcn/ui dialog
import { Button } from "@/components/ui/button"; // shadcn/ui button
import { Switch } from "@/components/ui/switch"; // shadcn/ui switch
import { Input } from "@/components/ui/input"; // shadcn/ui input
import { ScrollArea } from "@/components/ui/scroll-area"
import { CopyButton } from "@/components/ui/copy-button";
import { LoadingSpinner } from "../../components/loading-spinner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronDown, Trash2, DoorClosed, Share2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { toast } from 'react-toastify';

import { useCompetitionContext } from "../../providers/CompetitionContext";
import { getCompetitionCopy } from "../../lib/competitionCopy";
import { useTeamContext } from '../../providers/TeamContext';

/* --------------------------------------------
   TYPE DEFINITIONS
-------------------------------------------- */

interface League {
  id: number;
  name: string;
  isPublic: boolean;
  admin?: number;
  locked?: boolean;
  teams?: { id: number; name: string }[];
}

interface LeaguesData {
  totalPages: number;
  currentPage: number;
  data: League[];
}

interface LeagueTeam {
  teamId: number;
  teamName: string;
  rank: number;
  points: number;
  pin?: any; //todo change to be on leauge
}

interface LeagueResponse {
  totalPages: number;
  currentPage: number;
  data: LeagueTeam[];
  leagueAdmin?: number;
}

interface UserLeague {
  leagueId: number;
  leagueName: string;
  isPublic: boolean;
  admin?: number;
  locked?: boolean;
}

interface LeaguesProps {
  weekId: number;
}

/* --------------------------------------------
   DUMMY API CALLS (Replace with real fetch logic)
-------------------------------------------- */

async function fetchLeagues(competitionFeed: string, seasonId: number, page: number): Promise<LeaguesData> {
  const response = await fetch(
    `/api/leagues?competitionFeed=${competitionFeed}&seasonId=${seasonId}&pageRecords=10&pageNumber=${page}`
  )
  if (!response.ok) {
    throw new Error('Erreur réseau ligues')
  }
  return response.json()
}

async function fetchLeaguesSearch(
  competitionFeed: string,
  seasonId: number,
  page: number,
  searchTerm: string
): Promise<LeaguesData> {
  const response = await fetch(
    `/api/leagues/search?competitionFeed=${competitionFeed}&seasonId=${seasonId}&q=${encodeURIComponent(
      searchTerm
    )}&pageRecords=10&pageNumber=${page}`
  );
  if (!response.ok) {
    throw new Error("Erreur réseau ligues");
  }
  return response.json();
}

async function fetchLeague(
  leagueId: number,
  page: number,
  weekId: number | null,
  competitionFeed: string,
  seasonId: number
): Promise<LeagueResponse> {
  const params = new URLSearchParams();
  params.append('pageNumber', String(page));
  params.append('pageRecords', '10'); // Changed from 25 to 10 to match example
  params.append('competitionFeed', competitionFeed);
  params.append('seasonId', String(seasonId));

  if (weekId !== null) {
    params.append('weekId', String(weekId));
  }

  try {
    const url = `/api/league/${leagueId}?${params.toString()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Erreur réseau lors de la récupération de la ligue');
    }

    const jsonResponse = await response.json();
    
    // Transform the response to match our expected LeagueResponse interface
    const transformed: LeagueResponse = {
      totalPages: jsonResponse.totalPages || 1,
      currentPage: jsonResponse.currentPage || page,
      data: (jsonResponse.data || []).map((item: any) => ({
        teamId: item.teamId,
        teamName: item.teamName,
        rank: item.rank,
        points: item.points?.toString().replace('.00', '') || 0,
      })),
    };
    return transformed;
  } catch (error) {
    console.error('Error fetching league:', error);
    throw error;
  }
}

interface NewlyJoinedResponse {
  data: LeagueTeam[];
  pageRecords: number;
  hasMore: boolean;
  message?: string;
  rankingsCalculatedAt?: string | null;
}

async function fetchNewlyJoinedLeague(
  leagueId: number,
  page: number,
  weekId: number | null,
  competitionFeed: string,
  seasonId: number
): Promise<NewlyJoinedResponse> {
  const params = new URLSearchParams();
  params.append('pageNumber', String(page));
  params.append('pageRecords', '10');
  params.append('competitionFeed', competitionFeed);
  params.append('seasonId', String(seasonId));

  if (weekId !== null) {
    params.append('weekId', String(weekId));
  }

  const response = await fetch(`/api/league/${leagueId}/newly-joined?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Erreur réseau lors du chargement des nouvelles équipes');
  }

  const json = await response.json();
  return {
    data: (json.data || []).map((item: any) => ({
      teamId: item.teamId,
      teamName: item.teamName,
      rank: item.rank ?? null,
      points: item.points?.toString().replace('.00', '') || 0,
    })),
    pageRecords: json.pageRecords || 10,
    hasMore: Boolean(json.hasMore),
    message: json.message,
    rankingsCalculatedAt: json.rankingsCalculatedAt ?? null,
  };
}

interface CreateLeagueParams {
  name: string;
  isPublic: boolean;
  pinCode?: string;
  teamId: number;
}

export async function createLeagueRequest(
  params: CreateLeagueParams
): Promise<{ success: boolean; leagueId: number }> {
  const response = await fetch('/api/league', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || 'Échec de la création de la ligue. Réessayez.'
    );
  }

  return response.json();
}

export async function joinLeagueRequest(params: {
  leagueId: number;
  pinCode?: string;
  teamId: number;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/league/join', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let errorMessage = 'Erreur lors de l\'adhésion à la ligue';

    // Try to parse the error as JSON first
    try {
      const errorData = await response.json();
      console.log(errorData)
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      // If JSON parsing fails, try reading it as text
      try {
        errorMessage = await response.text();
        console.log(errorMessage)
      } catch (textError) {
        // If that also fails, we'll fall back to the default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

// New function to leave a league
export async function leaveLeagueRequest(params: {
  leagueId: number;
  teamId: number;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/league/leave', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let errorMessage = 'Erreur lors de la sortie de la ligue';

    // Try to parse the error as JSON first
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      // If JSON parsing fails, try reading it as text
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // If that also fails, we'll fall back to the default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

// New function to delete a league
export async function deleteLeagueRequest(leagueId: number): Promise<{ success: boolean }> {
  const response = await fetch(`/api/league/${leagueId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    let errorMessage = 'Erreur lors de la suppression de la ligue';

    // Try to parse the error as JSON first
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      // If JSON parsing fails, try reading it as text
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // If that also fails, we'll fall back to the default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function lockLeagueRequest(params: {
  leagueId: number;
  locked: boolean;
}): Promise<{ success: boolean; locked: boolean }> {
  const response = await fetch(`/api/league/${params.leagueId}/lock`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locked: params.locked }),
  });

  if (!response.ok) {
    let errorMessage = 'Erreur lors de la mise à jour du verrouillage';

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // fall back to default
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

const LEAGUE_MODAL_CLASS =
  "border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white shadow-[0_20px_50px_rgba(0,0,0,0.45)] [&>button]:text-white/70 [&>button]:hover:bg-white/10 [&>button]:hover:text-white";

const LEAGUE_MODAL_INPUT_CLASS =
  "border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-dark,#001425)] text-white placeholder:text-white/50 focus-visible:ring-[#FDC300]/40";

const LEAGUE_MODAL_OUTLINE_BTN_CLASS =
  "border-[var(--rtbf-blue,#0076A9)] bg-transparent text-white hover:bg-white/10 hover:text-white";

const LEAGUE_RADIO_UNSELECTED_CLASS =
  "flex cursor-pointer items-start gap-3 rounded-md border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-dark,#001425)] p-3 text-white";

const LEAGUE_RADIO_SELECTED_CLASS =
  "flex cursor-pointer items-start gap-3 rounded-md border border-[#FDC300] bg-[var(--rtbf-blue,#0076A9)] p-3 text-white";

/* --------------------------------------------
   LEAGUES PAGE COMPONENT
-------------------------------------------- */

export default function Leagues({ weekId }: LeaguesProps) {
  const queryClient = useQueryClient();
  const { data: session, status } = useSession();
  const { competitionConfig, competitionData } = useCompetitionContext();
  const copy = getCompetitionCopy(competitionConfig?.competitionFeed);
  const { team, isLoading } = useTeamContext();

  // Example user/team IDs from session (adjust to your session data shape)
  // const userId = session?.user?.userId ?? 1;
  // const teamId = Number(session?.user?.teamId);
  const userId = team?.userId || 0;
  const teamId = team?.id || 0;
  const teamQueryKey = ["team", String(teamId), "detail"] as const;

  const teamLeagues = useMemo(() => {
    if (!team || !team.leagues) return [];
    // Each element looks like { leagues: { id, name, isPublic } }
    // We'll map it to { leagueId, leagueName, isPublic }
    return team.leagues
    .filter((l: any) => { 
      return !l.minileagues?.exitWeek && l.leagues?.id
    })
    .map((item: any) => {
      const l = item.leagues;
      return {
        leagueId: l.id,
        leagueName: l.name,
        isPublic: !!l.isPublic,
        pinCode: l.pin,
        admin: l.admin,
        locked: !!l.locked
      };
    });
  }, [team]);

  // State for "create league" modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [pinCode, setPinCode] = useState("");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");

  // State for "join league" flow
  const [joinViewOpen, setJoinViewOpen] = useState(false);
  const [leaguesPageNumber, setLeaguesPageNumber] = useState(1);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [enterPin, setEnterPin] = useState("");

  // List of user's joined leagues & the selected league to display
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | null>(null);

  useEffect(() => {
    if (teamLeagues.length > 0 && selectedLeagueId === null) {
      setSelectedLeagueId(teamLeagues[0].leagueId);
      setLeagueTablePageNumber(1);
    }
  }, [teamLeagues, selectedLeagueId]);

  // State for leave league confirmation modal
  const [leaveLeagueModalOpen, setLeaveLeagueModalOpen] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState<{teamId: number, leagueId: number} | null>(null);
  
  // State for delete league confirmation modal
  const [deleteLeagueModalOpen, setDeleteLeagueModalOpen] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState<number | null>(null);

  const currentLeague = useMemo(() => {
    return teamLeagues.find((l :any) => l.leagueId === selectedLeagueId)
  }, [selectedLeagueId, teamLeagues]);

  // Pagination for the selected league's standings
  const [leagueTablePageNumber, setLeagueTablePageNumber] = useState(1);

  // Pagination for the "newly joined" teams table
  const [newlyJoinedPageNumber, setNewlyJoinedPageNumber] = useState(1);

  // Week selection for the league standings
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null);
  const [activeLeagueTab, setActiveLeagueTab] = useState<"general" | "week">("general");

  useEffect(() => {
    if (selectedWeekId !== null || !competitionData?.weeks?.length) return;
    const { deadlineWeek, displayWeek, rosterEndWeek } = competitionData?.deadlineInfo || {};
    const defaultWeekId =
      (typeof deadlineWeek === "number" ? deadlineWeek : null) ??
      (typeof displayWeek === "number" ? displayWeek : null) ??
      (typeof rosterEndWeek === "number" ? rosterEndWeek : null) ??
      competitionData.weeks[0]?.weekId ??
      null;
    setSelectedWeekId(defaultWeekId);
  }, [competitionData, selectedWeekId]);

  const effectiveWeekId = activeLeagueTab === "general" ? null : selectedWeekId;

  function leagueTabClassNames(...classes: (string | boolean | undefined)[]) {
    return classes.filter(Boolean).join(" ");
  }

  /* --------------------------------------------
     REACT QUERY HOOKS (v5 style)
  -------------------------------------------- */

  // 1) Leagues listing for "Join a league"
  const leaguesQuery = useQuery<LeaguesData, Error>({
    queryKey: ["leagues", leaguesPageNumber, activeSearchTerm],
    queryFn: () =>
      activeSearchTerm.trim() !== ""
        ? fetchLeaguesSearch(competitionConfig.competitionFeed, competitionConfig.seasonId, leaguesPageNumber, activeSearchTerm)
        : fetchLeagues(competitionConfig.competitionFeed, competitionConfig.seasonId, leaguesPageNumber),
    // keepPreviousData: true, // keep old data while fetching new
    // or refetchOnWindowFocus: false if you prefer
  });

  // const [leagues, setLeagues] = useState<LeaguesData | null>(null)

  // useEffect(() => {
  //   fetchLeagues(1)
  //     .then(data => setLeagues(data))
  //     .catch(err => console.error(err))
  // }, [])

  // 2) Specific league data (teams) for the selected league
  const leagueQuery = useQuery<LeagueResponse | null, Error>({
    queryKey: [
      "league",
      selectedLeagueId,
      leagueTablePageNumber,
      activeLeagueTab,
      effectiveWeekId,
      competitionConfig.competitionFeed,
      competitionConfig.seasonId,
    ],
    queryFn: () => {
      if (!selectedLeagueId) return Promise.resolve(null);
      return fetchLeague(
        selectedLeagueId, 
        leagueTablePageNumber, 
        effectiveWeekId, 
        competitionConfig.competitionFeed, 
        competitionConfig.seasonId
      );
    },
    // keepPreviousData: true,
    enabled:
      !!selectedLeagueId &&
      (activeLeagueTab === "general" || selectedWeekId !== null), // only fetch if we have a league
  });

  // 2b) Newly joined teams (not yet in the calculated leaderboard) for the selected league
  const newlyJoinedQuery = useQuery<NewlyJoinedResponse | null, Error>({
    queryKey: [
      "leagueNewlyJoined",
      selectedLeagueId,
      newlyJoinedPageNumber,
      activeLeagueTab,
      effectiveWeekId,
      competitionConfig.competitionFeed,
      competitionConfig.seasonId,
    ],
    queryFn: () => {
      if (!selectedLeagueId) return Promise.resolve(null);
      return fetchNewlyJoinedLeague(
        selectedLeagueId,
        newlyJoinedPageNumber,
        effectiveWeekId,
        competitionConfig.competitionFeed,
        competitionConfig.seasonId
      );
    },
    enabled:
      !!selectedLeagueId &&
      (activeLeagueTab === "general" || selectedWeekId !== null),
    retry: false,
  });

  useEffect(() => {
    setNewlyJoinedPageNumber(1);
  }, [selectedLeagueId, activeLeagueTab, effectiveWeekId]);

  // 3) Create league mutation
  const createLeagueMutation = useMutation<
    { success: boolean; leagueId: number },
    Error,
    {
      name: string;
      isPublic: boolean;
      pinCode?: string;
      userId: number;
      teamId: number;
    }
  >({
    mutationFn: createLeagueRequest,
    onSuccess: (data, variables) => {
      // Update the team query cache to include the newly created league.
      // This ensures that the dropdown is immediately aware of the new league.
      queryClient.setQueryData(teamQueryKey, (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        const newLeagueEntry = {
          leagues: {
            id: data.leagueId,
            name: variables.name,
            isPublic: variables.isPublic,
            admin: userId, // Set current user as admin
          },
        };
        return {
          ...oldTeamData,
          leagues: [...oldTeamData.leagues, newLeagueEntry],
        };
      });

      // Set the created league as the selected league in the dropdown.
      setSelectedLeagueId(data.leagueId);

      // Clear modal/form values and close the modal.
      setCreateModalOpen(false);
      setNewLeagueName("");
      setPinCode("");
      setIsPublic(false);

      // Optionally, revalidate team data.
      queryClient.invalidateQueries({ queryKey: teamQueryKey });
    },
  });

  // 4) Join league mutation
  const joinLeagueMutation = useMutation<
    { success: boolean },
    Error,
    { leagueId: number; pinCode?: string; userId: number; teamId: number }
  >({
    mutationFn: joinLeagueRequest,
    onSuccess: () => {
      if (selectedLeague) {
        // Immediately update the selected league in the dropdown
        setSelectedLeagueId(selectedLeague.id);
        
        // Optionally, update your team context/query cache immediately so that
        // the joined league appears in the dropdown without waiting for refetch.
        queryClient.setQueryData(teamQueryKey, (oldTeamData: any) => {
          if (!oldTeamData) return oldTeamData;
          // Prevent duplicate league entries
          if (
            oldTeamData.leagues.some(
              (l: any) => l.leagues.id === selectedLeague.id
            )
          ) {
            return oldTeamData;
          }
          return {
            ...oldTeamData,
            leagues: [...oldTeamData.leagues, { leagues: selectedLeague }],
          };
        });
      }
      setSelectedLeague(null);
      setJoinViewOpen(false);
      setEnterPin("");
      // Invalidate the team query to refetch updated data from the backend
      queryClient.invalidateQueries({ queryKey: teamQueryKey });
    },
  });

  // 5) Leave league mutation
  const leaveLeagueMutation = useMutation<
    { success: boolean },
    Error,
    { leagueId: number; teamId: number }
  >({
    mutationFn: leaveLeagueRequest,
    onSuccess: (_, variables) => {
      // Clear selected league if it was the one we just left
      if (selectedLeagueId === variables.leagueId) {
        setSelectedLeagueId(null);
      }

      // Update team context to remove the left league
      queryClient.setQueryData(teamQueryKey, (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        return {
          ...oldTeamData,
          leagues: oldTeamData.leagues.filter(
            (l: any) => l.leagues.id !== variables.leagueId
          ),
        };
      });

      // Remove from the teamLeagues array in state
      const updatedTeamLeagues = teamLeagues.filter((l: any) => l.leagueId !== variables.leagueId);
      queryClient.setQueryData(teamQueryKey, (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        return {
          ...oldTeamData,
          leagues: updatedTeamLeagues,
        };
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: teamQueryKey });
      queryClient.invalidateQueries({ queryKey: ["league", variables.leagueId] });

      toast.success("Vous avez quitté la ligue");
      setLeaveLeagueModalOpen(false);
      setTeamToRemove(null);
    },
    onError: (error) => {
      toast.error(`Erreur lors de la sortie de la ligue : ${error.message}`);
      setLeaveLeagueModalOpen(false);
      setTeamToRemove(null);
    }
  });
  
  // 6) Delete league mutation
  const deleteLeagueMutation = useMutation<
    { success: boolean },
    Error,
    number
  >({
    mutationFn: deleteLeagueRequest,
    onSuccess: (_, deletedLeagueId) => {
      // Clear selected league if it was the one we just deleted
      if (selectedLeagueId === deletedLeagueId) {
        setSelectedLeagueId(null);
      }

      // Update team context to remove the deleted league
      queryClient.setQueryData(teamQueryKey, (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        return {
          ...oldTeamData,
          leagues: oldTeamData.leagues.filter(
            (l: any) => l.leagues.id !== deletedLeagueId
          ),
        };
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: teamQueryKey });
      queryClient.invalidateQueries({ queryKey: ["leagues"] });

      toast.success("La ligue a été supprimée");
      setDeleteLeagueModalOpen(false);
      setLeagueToDelete(null);
    },
    onError: (error) => {
      toast.error(`Erreur lors de la suppression de la ligue : ${error.message}`);
      setDeleteLeagueModalOpen(false);
      setLeagueToDelete(null);
    }
  });

  // 7) Lock league mutation (admin only)
  const lockLeagueMutation = useMutation<
    { success: boolean; locked: boolean },
    Error,
    { leagueId: number; locked: boolean }
  >({
    mutationFn: lockLeagueRequest,
    onSuccess: (_, variables) => {
      queryClient.setQueryData(teamQueryKey, (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        return {
          ...oldTeamData,
          leagues: oldTeamData.leagues.map((l: any) => {
            if (l.leagues.id !== variables.leagueId) return l;
            return {
              ...l,
              leagues: {
                ...l.leagues,
                locked: variables.locked ? 1 : 0,
              },
            };
          }),
        };
      });

      queryClient.invalidateQueries({ queryKey: teamQueryKey });
      queryClient.invalidateQueries({ queryKey: ["leagues"] });
      toast.success(variables.locked ? "La ligue est désormais verrouillée" : "La ligue est désormais ouverte");
    },
    onError: (error) => {
      toast.error(`Erreur lors du verrouillage : ${error.message}`);
    },
  });

  /* --------------------------------------------
     HANDLERS
  -------------------------------------------- */
  function handleCreateLeague() {
    createLeagueMutation.mutate({
      name: newLeagueName,
      isPublic,
      pinCode: !isPublic ? pinCode : undefined,
      userId,
      teamId,
    });
  }

  function handleOpenJoinLeague() {
    setJoinViewOpen(true);
    setLeaguesPageNumber(1);
    void leaguesQuery.refetch();
  }

  function handleJoinLeague() {
    if (!selectedLeague) return;
    if (selectedLeague.locked) {
      toast.error("Cette ligue est verrouillée.");
      return;
    }

    console.log({leagueId: selectedLeague.id,
      pinCode: !selectedLeague.isPublic ? enterPin : undefined,
      userId,
      teamId,
    });
      
    joinLeagueMutation.mutate({
      leagueId: selectedLeague.id,
      pinCode: !selectedLeague.isPublic ? enterPin : undefined,
      userId,
      teamId,
    });
  }

  function handleLeaveLeague() {
    if (!teamToRemove) return;
    
    leaveLeagueMutation.mutate({
      leagueId: teamToRemove.leagueId,
      teamId: teamToRemove.teamId,
    });
  }

  function openLeaveLeagueModal(teamId: number, leagueId: number) {
    setTeamToRemove({ teamId, leagueId });
    setLeaveLeagueModalOpen(true);
  }
  
  function handleDeleteLeague() {
    if (leagueToDelete === null) return;
    
    deleteLeagueMutation.mutate(leagueToDelete);
  }
  
  function openDeleteLeagueModal(leagueId: number) {
    setLeagueToDelete(leagueId);
    setDeleteLeagueModalOpen(true);
  }

  function handleToggleLeagueLock(checked: boolean) {
    if (!selectedLeagueId) return;
    lockLeagueMutation.mutate({
      leagueId: selectedLeagueId,
      locked: checked,
    });
  }
  
  // Check if current user is the admin of the league
  function isLeagueAdmin(leagueAdminId?: number) {
    return userId === leagueAdminId;
  }

  function cancelSelectedLeague() {
    setSelectedLeague(null);
  }

  function selectLeagueForDetails(league: League) {
    setSelectedLeague(league);
  }

  function handleSelectUserLeague(leagueId: number) {
    setSelectedLeagueId(leagueId);
    setLeagueTablePageNumber(1);
  }

  function handleLeagueRankingTabChange(tab: "general" | "week") {
    setActiveLeagueTab(tab);
    setLeagueTablePageNumber(1);
  }

  // Check if current user can manage a team (is team owner or league admin)
  function canManageTeam(teamId: number, leagueAdminId?: number) {
    return teamId === team?.id || userId === leagueAdminId;
  }

  const leagueTabs = [
    { name: "Général", key: "general" as const, current: activeLeagueTab === "general" },
    { name: copy.raceLabelTitle, key: "week" as const, current: activeLeagueTab === "week" },
  ];

  /* --------------------------------------------
     RENDER
  -------------------------------------------- */

  if (status === "loading") {
    return <LoadingSpinner />;
  }

  if (!session) {
    return (
      <div className="bg-gpspel-page p-4 min-h-[calc(100vh-100px)] flex items-center justify-center">
        <div className="bg-white rounded-lg shadow p-8 text-center max-w-md">
          <h2 className="text-xl font-bold mb-4">Connexion requise</h2>
          <p className="text-gray-600 mb-6">Connectez-vous d'abord pour participer</p>
          <Button variant="green" onClick={() => signIn()}>
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  if (team == null || isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-[var(--rtbf-dark,#001425)] min-h-[calc(100vh-100px)] px-3 pb-32 pt-6 sm:px-4">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <section className="overflow-hidden rounded-lg bg-[var(--rtbf-blue-dark,#003E63)] text-white">
          <div className="p-4 md:p-6">
            <p className="text-md text-white/90">Défiez vos amis et affrontez-vous dans une ligue.</p>
          </div>
        </section>

        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
          <Button
            variant="outline"
            onClick={handleOpenJoinLeague}
            className="w-full rounded-full sm:w-auto"
          >
            Rejoindre une ligue
          </Button>
          <Button
            variant="default"
            onClick={() => setCreateModalOpen(true)}
            className="w-full rounded-full bg-[#FDC300] text-black hover:bg-yellow-300 sm:w-auto"
          >
            Créer une ligue
          </Button>
        </div>

        <div className="space-y-6">
          <div className="w-full">
            <label className="mb-1 block text-base font-semibold text-white">Vos ligues</label>
            <Select
              onValueChange={(value) => handleSelectUserLeague(Number(value))}
              value={selectedLeagueId ? String(selectedLeagueId) : ""}
            >
              <SelectTrigger className="w-full border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white">
                <SelectValue placeholder="Choisissez une ligue..." />
              </SelectTrigger>
              <SelectContent>
                {teamLeagues.map((l: any) => (
                  <SelectItem key={l.leagueId} value={String(l.leagueId)}>
                    {l.leagueName}
                  </SelectItem>
                ))}
                {teamLeagues.length === 0 && (
                  <p className="m-2 block text-sm">Vous n'etes pas encore dans une ligue</p>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedLeagueId && (
            <>
              <div className="relative grid grid-cols-1 sm:hidden">
                <select
                  value={activeLeagueTab}
                  onChange={(e) => handleLeagueRankingTabChange(e.target.value as "general" | "week")}
                  aria-label="Sélectionner un onglet"
                  className="w-full appearance-none rounded-full border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] py-2.5 pl-4 pr-10 text-base font-semibold text-white outline-none focus:ring-2 focus:ring-[#FDC300]/50"
                >
                  {leagueTabs.map((tab) => (
                    <option key={tab.key} value={tab.key}>
                      {tab.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-white/80"
                />
              </div>

              <div className="hidden sm:block">
                <nav aria-label="Tabs" className="-mb-px flex flex-wrap gap-6 border-b border-white/20">
                  {leagueTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => handleLeagueRankingTabChange(tab.key)}
                      aria-current={tab.current ? "page" : undefined}
                      className={leagueTabClassNames(
                        tab.current
                          ? "border-[#FDC300] text-white"
                          : "border-transparent text-white/70 hover:border-white/40 hover:text-white",
                        "whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-semibold transition-colors",
                      )}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              {activeLeagueTab === "week" && (
                <div className="w-full">
                  <Select
                    value={selectedWeekId != null ? String(selectedWeekId) : ""}
                    onValueChange={(value) => {
                      setLeagueTablePageNumber(1);
                      setSelectedWeekId(Number(value));
                    }}
                  >
                    <SelectTrigger
                      aria-label={copy.raceLabelTitle}
                      className="w-full border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white"
                    >
                      <SelectValue placeholder={`Choisissez une ${copy.raceLabelSingular}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {competitionData.weeks.map((week: any) => (
                        <SelectItem key={week.weekId} value={String(week.weekId)}>
                          {week.weekId}.{" "}
                          {competitionData.matches.find((match: any) => match.weekId === week.weekId)?.feedUrl}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="overflow-hidden rounded-md text-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-[var(--rtbf-blue,#0076A9)] [&_tr]:border-[var(--rtbf-blue,#0076A9)]">
                    <tr>
                      <th className="p-2 text-left font-medium text-white">Pos.</th>
                      <th className="px-4 py-2 text-left font-medium text-white">Nom</th>
                      <th className="px-4 py-2 text-right font-medium text-white">Points</th>
                      <th className="px-4 py-2 text-right font-medium text-white">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leagueQuery.isLoading ? (
                      <tr className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                        <td colSpan={4} className="py-8 text-center text-white/80">
                          Chargement...
                        </td>
                      </tr>
                    ) : !leagueQuery.data || leagueQuery.data.data.length === 0 ? (
                      <tr className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                        <td colSpan={4} className="py-6 text-center text-white/90">
                          Aucun résultat
                        </td>
                      </tr>
                    ) : (
                      leagueQuery.data.data.map((team, index) => (
                        <tr
                          key={team.teamId}
                          className={`${index % 2 === 0 ? "bg-[var(--rtbf-blue-dark,#003E63)]" : "bg-[var(--rtbf-blue,#0076A9)]"} border-b border-[var(--rtbf-blue,#0076A9)] text-white hover:bg-white/10`}
                        >
                          <td className="p-2">
                            {team.rank == null || team.rank === 0
                              ? "-"
                              : ((leagueQuery?.data?.currentPage || 1) - 1) * 10 + team.rank}
                          </td>
                          <td className="px-4 py-2">
                            <a href={`/fantasy/public/${team.teamId}`} className="font-medium text-white hover:text-[#FDC300] hover:underline">
                              {team.teamName}
                            </a>
                          </td>
                          <td className="px-4 py-2 text-right">{team.points}</td>
                          <td className="px-4 py-2 text-right">
                            {canManageTeam(team.teamId, currentLeague?.admin) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openLeaveLeagueModal(team.teamId, selectedLeagueId)}
                                className="text-red-400 hover:bg-white/10 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {leagueQuery.data && leagueQuery.data.data.length > 0 && (
                <div className="flex flex-col items-center justify-between gap-4 p-2 sm:flex-row">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openLeaveLeagueModal(teamId, selectedLeagueId)}
                    className="w-full text-red-400 hover:bg-white/10 hover:text-red-300 sm:w-auto"
                  >
                    <DoorClosed className="h-4 w-4" /> Quitter la ligue
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
                      onClick={() => setLeagueTablePageNumber((p) => Math.max(1, p - 1))}
                      disabled={leagueTablePageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-white/90">
                      Page {leagueQuery.data.currentPage} sur {leagueQuery.data.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
                      onClick={() =>
                        setLeagueTablePageNumber((p) => Math.min(leagueQuery.data?.totalPages ?? 1, p + 1))
                      }
                      disabled={leagueTablePageNumber === (leagueQuery.data?.totalPages ?? 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {(newlyJoinedQuery.data?.data.length ?? 0) > 0 && (
                <div className="space-y-2 text-white">
                  <div>
                    <h2 className="text-base font-semibold">Nouvelles équipes</h2>
                    <p className="text-sm text-white/90">
                      Ces équipes rejoindront le classement après la prochaine journée.
                    </p>
                  </div>
                  <div className="overflow-hidden rounded-md text-white">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[var(--rtbf-blue,#0076A9)] [&_tr]:border-[var(--rtbf-blue,#0076A9)]">
                        <tr>
                          <th className="p-2 text-left font-medium text-white">Pos.</th>
                          <th className="px-4 py-2 text-left font-medium text-white">Nom</th>
                          <th className="px-4 py-2 text-right font-medium text-white">Points</th>
                          <th className="px-4 py-2 text-right font-medium text-white">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newlyJoinedQuery.data!.data.map((team, index) => (
                          <tr
                            key={team.teamId}
                            className={`${index % 2 === 0 ? "bg-[var(--rtbf-blue-dark,#003E63)]" : "bg-[var(--rtbf-blue,#0076A9)]"} border-b border-[var(--rtbf-blue,#0076A9)] text-white hover:bg-white/10`}
                          >
                            <td className="p-2">-</td>
                            <td className="px-4 py-2">
                              <a href={`/fantasy/public/${team.teamId}`} className="font-medium text-white hover:text-[#FDC300] hover:underline">
                                {team.teamName}
                              </a>
                            </td>
                            <td className="px-4 py-2 text-right">{team.points}</td>
                            <td className="px-4 py-2 text-right">
                              {canManageTeam(team.teamId, currentLeague?.admin) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openLeaveLeagueModal(team.teamId, selectedLeagueId)}
                                  className="text-red-400 hover:bg-white/10 hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(newlyJoinedPageNumber > 1 || newlyJoinedQuery.data!.hasMore) && (
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
                        onClick={() => setNewlyJoinedPageNumber((p) => Math.max(1, p - 1))}
                        disabled={newlyJoinedPageNumber === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
                        onClick={() => setNewlyJoinedPageNumber((p) => p + 1)}
                        disabled={!newlyJoinedQuery.data!.hasMore}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {leagueQuery.data && isLeagueAdmin(currentLeague?.admin) && (
                <div className="space-y-1">
                  <h2 className="text-base font-semibold text-white">Actions</h2>
                  <div className="flex flex-wrap items-center gap-3 rounded-lg bg-[var(--rtbf-blue-dark,#003E63)] p-4">
                    <label className="flex items-center gap-2 text-sm text-white">
                      <Switch
                        checked={!!currentLeague?.locked}
                        onCheckedChange={handleToggleLeagueLock}
                        disabled={lockLeagueMutation.isPending}
                      />
                      {currentLeague?.locked ? "Ligue verrouillée" : "Ligue ouverte"}
                    </label>
                    <Button variant="destructive" size="sm" onClick={() => openDeleteLeagueModal(selectedLeagueId)}>
                      Supprimer la ligue
                    </Button>
                  </div>
                </div>
              )}

              {currentLeague && (
                <div className="space-y-1">
                  <h2 className="text-base font-semibold text-white">Invitez vos amis en partageant le texte ci-dessous</h2>
                  <section className="overflow-hidden rounded-lg bg-[var(--rtbf-blue-dark,#003E63)] text-white">
                    <div className="p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-md whitespace-pre-line text-white/90">
                          Rejoignez ma ligue "{currentLeague.leagueName}" dans "On connaît nos classiques, le jeu"
                          via {typeof window !== "undefined" ? window.location.hostname : ""}{" "}
                          {currentLeague.pinCode ? ` Code PIN : ${currentLeague.pinCode}` : ""}
                        </p>
                        <CopyButton
                          variant="outline"
                          className="rounded-lg border-[#FDC300] bg-[#FDC300] px-5 text-black hover:bg-yellow-300"
                          size="lg"
                          label="Partager l'invitation"
                          icon={<Share2 className="size-5 shrink-0" aria-hidden />}
                          value={`Rejoignez ma ligue "${currentLeague.leagueName}" dans "On connaît nos classiques, le jeu" via ${
                            typeof window !== "undefined" ? window.location.hostname : ""
                          } ${currentLeague.pinCode ? ` Code PIN : ${currentLeague.pinCode}` : ""}`}
                        />
                      </div>
                    </div>
                  </section>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* CREATE LEAGUE MODAL */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className={LEAGUE_MODAL_CLASS}>
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-white">Créer une ligue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Nom de la ligue"
              value={newLeagueName}
              onChange={(e) => setNewLeagueName(e.target.value)}
              className={LEAGUE_MODAL_INPUT_CLASS}
            />
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-white">Type de ligue</legend>
              <label className={isPublic ? LEAGUE_RADIO_SELECTED_CLASS : LEAGUE_RADIO_UNSELECTED_CLASS}>
                <input
                  type="radio"
                  name="leagueType"
                  checked={isPublic}
                  onChange={() => setIsPublic(true)}
                  className="h-4 w-4 self-center accent-[#FDC300]"
                />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-white">
                    Ligue publique
                  </p>
                  <p className="text-xs text-white/80">
                    Tout le monde peut trouver et rejoindre cette ligue.
                  </p>
                </div>
              </label>
              <label className={!isPublic ? LEAGUE_RADIO_SELECTED_CLASS : LEAGUE_RADIO_UNSELECTED_CLASS}>
                <input
                  type="radio"
                  name="leagueType"
                  checked={!isPublic}
                  onChange={() => setIsPublic(false)}
                  className="h-4 w-4 self-center accent-[#FDC300]"
                />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-white">
                    Ligue privée
                  </p>
                  <p className="text-xs text-white/80">
                    Les joueurs ne peuvent rejoindre qu'avec un code unique.
                  </p>
                </div>
              </label>
            </fieldset>
            {!isPublic && (
              <div className="space-y-2">
                <Input
                  placeholder="Code PIN à 4 chiffres"
                  maxLength={4}
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value)}
                  className={LEAGUE_MODAL_INPUT_CLASS}
                />
                <p className="text-xs text-white/70">Choisissez un code à 4 chiffres pour protéger l’accès à votre ligue.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className={LEAGUE_MODAL_OUTLINE_BTN_CLASS} onClick={() => setCreateModalOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="default"
              className="bg-[#FDC300] text-black hover:bg-yellow-300"
              onClick={handleCreateLeague}
              disabled={!newLeagueName || createLeagueMutation.isPending}
            >
              {createLeagueMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* JOIN LEAGUE OVERVIEW MODAL */}
      <Dialog open={joinViewOpen} onOpenChange={setJoinViewOpen}>
        <DialogContent className={`max-w-xl ${LEAGUE_MODAL_CLASS}`}>
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-white">Rejoindre une ligue</DialogTitle>
          </DialogHeader>
          {selectedLeague && (
            <div className="rounded-md border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-dark,#001425)] p-3">
              <h2 className="mb-2 text-base font-semibold text-white">{selectedLeague.name}</h2>
              {selectedLeague.teams && selectedLeague.teams.length > 0 && (
                <ul className="mb-2 list-inside list-disc text-white/90">
                  {selectedLeague.teams.map((t) => (
                    <li key={t.id}>{t.name}</li>
                  ))}
                </ul>
              )}
              {!!selectedLeague.locked && (
                <div className="mb-2 rounded-md border border-red-500/30 bg-red-950/40 px-3 py-2 text-sm text-red-300">
                  Cette ligue est verrouillée. Vous ne pouvez plus la rejoindre.
                </div>
              )}
              {!selectedLeague.isPublic && (
                <Input
                  placeholder="Saisir le code PIN"
                  maxLength={4}
                  value={enterPin}
                  onChange={(e) => setEnterPin(e.target.value)}
                  className={`mb-2 ${LEAGUE_MODAL_INPUT_CLASS}`}
                  disabled={!!selectedLeague.locked}
                />
              )}
              <div className="flex items-center gap-2">
                <Button variant="outline" className={LEAGUE_MODAL_OUTLINE_BTN_CLASS} onClick={cancelSelectedLeague}>
                  Annuler
                </Button>
                <Button
                  variant="default"
                  className="bg-[#FDC300] text-black hover:bg-yellow-300"
                  onClick={handleJoinLeague}
                  disabled={!!selectedLeague.locked || joinLeagueMutation.isPending}
                >
                  {joinLeagueMutation.isPending ? "Connexion..." : "Rejoindre"}
                </Button>
              </div>
            </div>
          )}
          {!selectedLeague && (
            <div className="space-y-4">
              <div className="mb-4 flex items-center">
                <Input
                  placeholder="Rechercher une ligue..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setLeaguesPageNumber(1);
                  }}
                  className={`mr-4 flex-grow ${LEAGUE_MODAL_INPUT_CLASS}`}
                />
                <Button
                  variant="default"
                  className="ml-2 bg-[#FDC300] text-black hover:bg-yellow-300"
                  onClick={() => setActiveSearchTerm(searchTerm.trim())}
                  disabled={searchTerm.trim().length < 4}
                >
                  Rechercher
                </Button>
              </div>
              {leaguesQuery.isLoading && (
                <div className="flex items-center justify-center gap-2 py-8 text-white/70">
                  Chargement des ligues...
                </div>
              )}
              {!leaguesQuery.isLoading && leaguesQuery.data && (
                <>
                  <table className="min-w-full divide-y divide-[var(--rtbf-blue,#0076A9)] text-sm">
                    <thead className="bg-[var(--rtbf-blue,#0076A9)]">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-white">Nom</th>
                        <th className="px-4 py-2 text-left font-medium text-white">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaguesQuery.data.data.map((league, index) => (
                        <tr
                          key={league.id}
                          onClick={() => {
                            if (!league.locked) {
                              selectLeagueForDetails(league);
                            }
                          }}
                          className={`${league.locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-white/10'} ${index % 2 === 0 ? 'bg-[var(--rtbf-blue-dark,#003E63)] text-white' : 'bg-[var(--rtbf-blue,#0076A9)] text-white'} border-b border-[var(--rtbf-blue,#0076A9)]`}
                        >
                          <td className="px-4 py-2">{league.name}</td>
                          <td className="px-4 py-2">
                            {league.locked ? (
                              <span className="text-xs font-semibold text-red-400">Fermée</span>
                            ) : (
                              <span className="text-xs font-semibold text-white/90">
                                {league.isPublic ? "Publique" : "Privée"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-end gap-2 p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
                      onClick={() => {
                        if (leaguesPageNumber > 1) {
                          setLeaguesPageNumber((p) => p - 1);
                          void leaguesQuery.refetch();
                        }
                      }}
                      disabled={leaguesPageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-white/90">
                      Page {leaguesQuery.data.currentPage} sur {leaguesQuery.data.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200"
                      onClick={() => {
                        if (leaguesPageNumber < leaguesQuery.data.totalPages) {
                          setLeaguesPageNumber((p) => p + 1);
                          void leaguesQuery.refetch();
                        }
                      }}
                      disabled={leaguesPageNumber === leaguesQuery.data.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* LEAVE LEAGUE CONFIRMATION MODAL */}
      <Dialog open={leaveLeagueModalOpen} onOpenChange={setLeaveLeagueModalOpen}>
        <DialogContent className={LEAGUE_MODAL_CLASS}>
          <div className="py-4 text-center text-white">
            <p>Etes-vous sur de vouloir quitter cette ligue ?</p>
            <p className="mt-2 text-sm text-white/70">Vous ne pourrez plus revenir. Cette action est irreversible.</p>
          </div>
          <DialogFooter className="justify-center sm:justify-center">
            <Button variant="outline" className={LEAGUE_MODAL_OUTLINE_BTN_CLASS} onClick={() => setLeaveLeagueModalOpen(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleLeaveLeague}
              disabled={leaveLeagueMutation.isPending}
            >
              {leaveLeagueMutation.isPending ? "Départ en cours..." : "Quitter la ligue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* DELETE LEAGUE CONFIRMATION MODAL */}
      <Dialog open={deleteLeagueModalOpen} onOpenChange={setDeleteLeagueModalOpen}>
        <DialogContent className={LEAGUE_MODAL_CLASS}>
          <div className="py-4 text-center text-white">
            <p className="font-semibold text-red-400">Attention : Cette action est irreversible !</p>
            <p className="mt-2">Etes-vous sur de vouloir supprimer cette ligue ?</p>
            <p className="mt-2 text-sm text-white/70">Cela supprimera definitivement la ligue pour tous les membres.</p>
          </div>
          <DialogFooter className="justify-center sm:justify-center">
            <Button variant="outline" className={LEAGUE_MODAL_OUTLINE_BTN_CLASS} onClick={() => setDeleteLeagueModalOpen(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteLeague}
              disabled={deleteLeagueMutation.isPending}
            >
              {deleteLeagueMutation.isPending ? "Suppression..." : "Supprimer la ligue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
